/**
 * @fileoverview Custom pipeline field display helpers (MOR-948)
 *
 * The icon set, value formatting and shared field shape. Kept apart from
 * the badge component so this module stays free of components (fast refresh
 * only works when a file exports components exclusively).
 *
 * Icons are a curated lucide subset rather than the full set: the picker
 * stays scannable, and every name is statically imported so no dynamic
 * import lands in the bundle.
 */

import {
  Banknote,
  Briefcase,
  Calendar,
  CalendarClock,
  CircleDollarSign,
  Clock,
  DollarSign,
  FileText,
  Flag,
  Gavel,
  Hash,
  Landmark,
  MapPin,
  Percent,
  PiggyBank,
  Receipt,
  Scale,
  Star,
  Tag,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { PreAppointmentFieldType } from "@cvx/preAppointment/preAppointmentFields/schema";

/** Curated icon set offered in the field picker, keyed by stored name. */
export const PRE_APPOINTMENT_FIELD_ICONS: Record<string, LucideIcon> = {
  tag: Tag,
  "dollar-sign": DollarSign,
  "circle-dollar-sign": CircleDollarSign,
  banknote: Banknote,
  "piggy-bank": PiggyBank,
  receipt: Receipt,
  percent: Percent,
  hash: Hash,
  calendar: Calendar,
  "calendar-clock": CalendarClock,
  clock: Clock,
  "trending-up": TrendingUp,
  target: Target,
  flag: Flag,
  star: Star,
  users: Users,
  briefcase: Briefcase,
  "file-text": FileText,
  scale: Scale,
  gavel: Gavel,
  landmark: Landmark,
  "map-pin": MapPin,
};

export const PRE_APPOINTMENT_FIELD_ICON_NAMES = Object.keys(
  PRE_APPOINTMENT_FIELD_ICONS,
);

/** The icon for a field, or the neutral tag when none was chosen. */
export function fieldIcon(icon: string | undefined): LucideIcon {
  return (icon && PRE_APPOINTMENT_FIELD_ICONS[icon]) || Tag;
}

/** Minimal shape the display helpers need — matches `fields.listByOrg`. */
export interface CustomFieldDef {
  _id: string;
  name: string;
  type: PreAppointmentFieldType;
  color: string;
  icon?: string;
}

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});
const NUM = new Intl.NumberFormat("en-AU");

/**
 * A stored value rendered for display. Values are stored as strings and
 * interpreted by the field's type; anything that fails to parse falls back
 * to the raw string rather than showing "NaN" over the practitioner's data.
 */
export function formatCustomFieldValue(
  type: PreAppointmentFieldType,
  value: string,
): string {
  if (type === "currency" || type === "number") {
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    return type === "currency" ? AUD.format(n) : NUM.format(n);
  }
  if (type === "date") {
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  return value;
}

/** Only numeric types can be summed across jobs. */
export function isSummable(type: PreAppointmentFieldType): boolean {
  return type === "currency" || type === "number";
}

/**
 * Per-field totals across a set of jobs (MOR-948). Only summable fields
 * (currency, number) produce a total; a field no job in the set carries a
 * value for is dropped, so an empty stage shows nothing rather than "$0".
 */
export function customFieldTotals(
  fields: CustomFieldDef[],
  jobs: Array<{ preAppointmentCustomFields: Record<string, string> }>,
): Array<{ field: CustomFieldDef; total: number }> {
  const totals: Array<{ field: CustomFieldDef; total: number }> = [];
  for (const field of fields) {
    if (!isSummable(field.type)) continue;
    const { total, seen } = jobs.reduce(
      (acc, job) => {
        const raw = job.preAppointmentCustomFields[field._id];
        if (!raw) return acc;
        const n = Number(raw);
        if (!Number.isFinite(n)) return acc;
        return { total: acc.total + n, seen: acc.seen + 1 };
      },
      { total: 0, seen: 0 },
    );
    if (seen > 0) totals.push({ field, total });
  }
  return totals;
}
